import { Suspense } from "react";
import { Routes, Route, Outlet, useNavigate } from "react-router-dom";
// Material-UI imports
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";

// MSAL imports
import { MsalProvider, MsalAuthenticationTemplate } from "@azure/msal-react";
import { IPublicClientApplication, InteractionType } from "@azure/msal-browser";
import { CustomNavigationClient } from "./utils/NavigationClient";
import { loginRequest } from "./utils/authConfig";
import { isE2EAuthBypass } from "./utils/e2eAuth";

// Sample app imports
import { PageLayout, AuthLoading, ErrorComponent } from "./modules/common/components";
import { CssBaseline, Stack } from "@mui/material";
import { Home } from "./modules/home/pages";
import { Company, Centers, CenterDetail, PhysicianGroups, DiagnosisCodes } from "./modules/configuration/pages";
import { MyAccess, Profile, ProfileEdit } from "./modules/profile/pages";
import Schedule from "./modules/encounter/pages/Schedule";
import EncounterCreate from "./modules/encounter/pages/EncounterCreate";
import EncounterEdit from "./modules/encounter/pages/EncounterEdit";
import { MedicalRecordsRequestRoute, MedicalRecordsRequestsRoute, MedicalRecordsRoute } from "./modules/encounter/features/medicalRecords";
import { RegistrationCreate, RegistrationEdit, Registration, PatientLookup } from "./modules/registration/pages";
import { Policies, PolicyCreate, PolicyEdit, CasemanWorklist, CaseManEdit } from "./modules/casemanagement/pages";
import { RefundCreate, RefundEdit, RefundWorklist, RefundLookup, RefundMyApprovals, RefundDashboard } from "./modules/refunds/pages";
import { WriteoffCreate, WriteoffEdit, WriteoffDashboard, WriteoffLookup, WriteoffWorklist, WriteoffMyApprovals } from "./modules/writeoffs/pages";
import { ContractList, ContractCreate, ContractEdit, ContractEstimate, FeeScheduleEdit } from "./modules/contracts/pages";
import { AppealsWorklist, AppealCreate, AppealEdit } from "./modules/appeals/pages";
import {
    LaborBudgetRouteElement,
    LaborRulesRouteElement,
    LaborProjectionsRouteElement,
} from "./modules/labor";
import { MedNecDashboard, MedNecCaseCreate, MedNecCaseDetail, MedNecAnalytics, MedNecChat, MedNecWorklist } from "./modules/mednec";
import {
    FC_DASHBOARD_PATH,
    FC_WORKLIST_ROUTE_PATH,
    FC_AGENT_DASHBOARD_PATH,
    FC_ASSIGNMENT_MANAGEMENT_PATH,
    FC_VERIFICATION_DETAIL_ROUTE_PATH,
    FC_SEARCH_PATH,
    FC_PREQUOTES_PATH,
    FC_PREQUOTE_DETAIL_ROUTE_PATH,
    VerificationDashboardRouteElement,
    VerificationWorklistRouteElement,
    AssignmentManagementRouteElement,
    VerificationDetailRouteElement,
    CaseSearchRouteElement,
    PreQuoteListRouteElement,
    PreQuoteDetailRouteElement,
    LegacyAgentDashboardRedirect,
} from "./modules/financialClearance/routes";
import {
    AGENT_DETAIL_ROUTE_PATH,
    AGENTS_BASE_PATH,
    AgentCatalogRouteElement,
    AgentDetailRouteElement,
} from "./modules/agents/routes";
import {
    CLAIMS_QUEUE_PATH,
    CLAIMS_EXCEPTIONS_PATH,
    CLAIMS_MISSED_FINDINGS_PATH,
    CLAIMS_DETAIL_PATH,
    CLAIMS_LINKED_CLAIM_DETAIL_PATH,
    ClaimsQueueRouteElement,
    ExceptionsQueueRouteElement,
    MissedFindingsRouteElement,
    ClaimDetailRouteElement,
    LinkedClaimDetailRouteElement,
} from "./modules/claims/routes";
import {
    PHYSICIAN_REQUESTS_QUEUE_PATH,
    PHYSICIAN_REQUESTS_REVIEW_PATH,
    StaffQueueRouteElement,
    StaffReviewRouteElement,
} from "./modules/physicianRequests/routes";
import {
    REVREC_HOME_PATH,
    REVREC_CENTER_PATH,
    REVREC_PERIOD_SUMMARY_PATH,
    REVREC_ACCOUNT_DETAIL_PATH,
    REVREC_OUT_OF_PERIOD_PATH,
    REVREC_PENDING_APPROVALS_PATH,
    REVREC_ADJUSTMENT_UPLOAD_PATH,
    REVREC_CENTER_ADJUSTMENT_UPLOAD_PATH,
    RevRecHomeRouteElement,
    CenterDashboardRouteElement,
    PeriodSummaryRouteElement,
    AccountDetailRouteElement,
    OutOfPeriodRouteElement,
    PendingApprovalsRouteElement,
    AdjustmentUploadRouteElement,
} from "./modules/revenueRecognition/routes";
import {
    DENIALS_ADMIN_PATH,
    DENIALS_ITEM_PATH,
    DENIALS_NEEDS_INFO_PATH,
    DENIALS_QUEUE_PATH,
    DENIALS_REPORT_PATH,
    DENIALS_RULES_PATH,
    DENIALS_RUN_REPORT_PATH,
    DENIALS_WORKLIST_PATH,
    DenialItemDetailRouteElement,
    DenialsAdminRouteElement,
    DenialsNeedsInfoRouteElement,
    DenialsQueueRouteElement,
    DenialsReportRouteElement,
    DenialsRulesRouteElement,
    DenialsRunReportRouteElement,
    DenialsWorklistRouteElement,
    LEGACY_DENIAL_DETAIL_PATH,
    LEGACY_DENIAL_WORKLIST_PATH,
    LegacyDenialsRedirect,
} from "./modules/denials";
import {
    SURGERY_BOARD_PATH,
    BLOCK_SCHEDULE_PATH,
    SurgeryBoardRouteElement,
    BlockScheduleRouteElement,
} from "./modules/surgeryBoard";

type AppProps = {
    pca: IPublicClientApplication;
};

function App({ pca }: AppProps) {
    // The next 3 lines are optional. This is how you configure MSAL to take advantage of the router's navigate functions when MSAL redirects between pages in your app
    const navigate = useNavigate();
    const navigationClient = new CustomNavigationClient(navigate);
    pca.setNavigationClient(navigationClient);

    return (
        <MsalProvider instance={pca}>
            <PageLayout>
                    <Grid container justifyContent='flex-start'>
                        <Pages />
                    </Grid>
            </PageLayout>
        </MsalProvider>
    );
}

// Forces an interactive login (redirect) for any nested route when the user is
// not authenticated. Protects deep links / bookmarks (e.g. /labor-management/projections)
// from rendering without a signed-in account. The public landing page ("/") is
// intentionally left outside this guard.
function ProtectedLayout() {
    // E2E-only bypass (inert in production): skip the interactive-login gate so Playwright can
    // exercise authenticated routes against mocked APIs.
    if (isE2EAuthBypass()) {
        return <Outlet />;
    }
    return (
        <MsalAuthenticationTemplate
            interactionType={InteractionType.Redirect}
            authenticationRequest={{ ...loginRequest }}
            errorComponent={ErrorComponent}
            loadingComponent={AuthLoading}
        >
            <Outlet />
        </MsalAuthenticationTemplate>
    );
}

function Pages() {
    return (
        <Routes>
            {/* Public landing: shows the logged-out marketing page or the dashboard. */}
            <Route path="/" element={<Home />} />

            {/* Everything below requires an authenticated session. */}
            <Route element={<ProtectedLayout />}>
            <Route path="/profile" element={<Profile />} />
            <Route path="/profile-edit" element={<ProfileEdit />} />
            <Route path='/company' element={<Company/>} />
            <Route path='/diagnosis-codes' element={<DiagnosisCodes/>} />
            <Route path="/centers/:company" element={<Centers />} />
            <Route path="/physician-groups/:company" element={<PhysicianGroups />} />
            <Route path="/my-access" element={<MyAccess />} />
            <Route path="/center-detail/:id" element={<CenterDetail />} />
            <Route path="/schedule" element={<Schedule />} />
            <Route path="/medical-records" element={<MedicalRecordsRoute />} />
            <Route path="/medical-records/request" element={<MedicalRecordsRequestRoute />} />
            <Route path="/medical-records/requests" element={<MedicalRecordsRequestsRoute />} />
            <Route path="/PatientLookup" element={<PatientLookup />} />
            <Route path="/encounterCreate" element={<EncounterCreate />} />
            <Route path="/encounterEdit" element={<EncounterEdit />} />
            <Route path="/RegistrationCreate" element={<RegistrationCreate />} />
            <Route path="/RegistrationEdit" element={<RegistrationEdit />} />
            <Route path="/Registration" element={<Registration />} />
            <Route path="/case-management/policies" element={<Policies />} />
            <Route path="/case-management/policy-create" element={<PolicyCreate />} />
            <Route path="/case-management/policies/edit" element={<PolicyEdit />} />
            <Route path="/case-management/worklist" element={<CasemanWorklist />} />
            <Route path="/case-management/case-edit" element={<CaseManEdit />} />
            <Route path="/refunds/create" element={<RefundCreate />} />
            <Route path="/refunds/edit" element={<RefundEdit />} />
            <Route path="/refunds/worklist" element={<RefundWorklist />} />
            <Route path="/refunds/lookup" element={<RefundLookup />} />
            <Route path="/refunds/my-approvals" element={<RefundMyApprovals />} />
            <Route path="/refunds/dashboard" element={<RefundDashboard />} />
            <Route path="/writeoffs/create" element={<WriteoffCreate />} />
            <Route path="/writeoffs/edit" element={<WriteoffEdit />} />
            <Route path="/writeoffs/lookup" element={<WriteoffLookup />} />
            <Route path="/writeoffs/worklist" element={<WriteoffWorklist />} />
            <Route path="/writeoffs/my-approvals" element={<WriteoffMyApprovals />} />
            <Route path="/writeoffs/dashboard" element={<WriteoffDashboard />} />
            <Route path="/contracts" element={<ContractList />} />
            <Route path="/contracts/create" element={<ContractCreate />} />
            <Route path="/contracts/edit" element={<ContractEdit />} />
            <Route path="/contracts/estimate" element={<ContractEstimate />} />
            <Route path="/feeschedules/edit" element={<FeeScheduleEdit />} />
            <Route path="/appeals" element={<AppealsWorklist />} />
            <Route path={LEGACY_DENIAL_WORKLIST_PATH} element={<LegacyDenialsRedirect />} />
            <Route path={LEGACY_DENIAL_DETAIL_PATH} element={<LegacyDenialsRedirect />} />
            <Route path="/appeals/create" element={<AppealCreate />} />
            <Route path="/appeals/edit/:id" element={<AppealEdit />} />
            <Route path="/labor-management/budget" element={<LaborBudgetRouteElement />} />
            <Route path="/labor-management/rules" element={<LaborRulesRouteElement />} />
            <Route path="/labor-management/projections" element={<LaborProjectionsRouteElement />} />
            <Route path="/mednec" element={<MedNecDashboard />} />
            <Route path="/mednec/worklist" element={<MedNecWorklist />} />
            <Route path="/mednec/cases/new" element={<MedNecCaseCreate />} />
            <Route path="/mednec/cases/edit" element={<MedNecCaseDetail />} />
            <Route path="/mednec/analytics" element={<MedNecAnalytics />} />
            <Route path="/mednec/chat" element={<MedNecChat />} />
            <Route path={FC_DASHBOARD_PATH} element={<VerificationDashboardRouteElement />} />
            <Route path={FC_WORKLIST_ROUTE_PATH} element={<VerificationWorklistRouteElement />} />
            <Route path={FC_AGENT_DASHBOARD_PATH} element={<LegacyAgentDashboardRedirect />} />
            <Route path={FC_ASSIGNMENT_MANAGEMENT_PATH} element={<AssignmentManagementRouteElement />} />
            <Route path={FC_VERIFICATION_DETAIL_ROUTE_PATH} element={<VerificationDetailRouteElement />} />
            <Route path={FC_SEARCH_PATH} element={<CaseSearchRouteElement />} />
            <Route path={FC_PREQUOTES_PATH} element={<PreQuoteListRouteElement />} />
            <Route path={FC_PREQUOTE_DETAIL_ROUTE_PATH} element={<PreQuoteDetailRouteElement />} />
            <Route path={AGENTS_BASE_PATH} element={<AgentCatalogRouteElement />} />
            <Route path={AGENT_DETAIL_ROUTE_PATH} element={<AgentDetailRouteElement />} />
            <Route path={CLAIMS_QUEUE_PATH} element={<ClaimsQueueRouteElement />} />
            <Route path={CLAIMS_EXCEPTIONS_PATH} element={<ExceptionsQueueRouteElement />} />
            <Route path={CLAIMS_MISSED_FINDINGS_PATH} element={<MissedFindingsRouteElement />} />
            <Route path={CLAIMS_DETAIL_PATH} element={<ClaimDetailRouteElement />} />
            <Route path={CLAIMS_LINKED_CLAIM_DETAIL_PATH} element={<LinkedClaimDetailRouteElement />} />
            <Route path={PHYSICIAN_REQUESTS_QUEUE_PATH} element={<StaffQueueRouteElement />} />
            <Route path={PHYSICIAN_REQUESTS_REVIEW_PATH} element={<StaffReviewRouteElement />} />
            <Route path={REVREC_HOME_PATH} element={<RevRecHomeRouteElement />} />
            <Route path={REVREC_CENTER_PATH} element={<CenterDashboardRouteElement />} />
            <Route path={REVREC_PERIOD_SUMMARY_PATH} element={<PeriodSummaryRouteElement />} />
            <Route path={REVREC_ACCOUNT_DETAIL_PATH} element={<AccountDetailRouteElement />} />
            <Route path={REVREC_OUT_OF_PERIOD_PATH} element={<OutOfPeriodRouteElement />} />
            <Route path={REVREC_PENDING_APPROVALS_PATH} element={<PendingApprovalsRouteElement />} />
            <Route path={REVREC_ADJUSTMENT_UPLOAD_PATH} element={<AdjustmentUploadRouteElement />} />
            <Route path={REVREC_CENTER_ADJUSTMENT_UPLOAD_PATH} element={<AdjustmentUploadRouteElement />} />
            <Route path={DENIALS_QUEUE_PATH} element={<DenialsQueueRouteElement />} />
            <Route path={DENIALS_WORKLIST_PATH} element={<DenialsWorklistRouteElement />} />
            <Route path={DENIALS_ITEM_PATH} element={<DenialItemDetailRouteElement />} />
            <Route path={DENIALS_RUN_REPORT_PATH} element={<DenialsRunReportRouteElement />} />
            <Route path={DENIALS_REPORT_PATH} element={<DenialsReportRouteElement />} />
            <Route path={DENIALS_ADMIN_PATH} element={<DenialsAdminRouteElement />} />
            <Route path={DENIALS_RULES_PATH} element={<DenialsRulesRouteElement />} />
            <Route path={DENIALS_NEEDS_INFO_PATH} element={<DenialsNeedsInfoRouteElement />} />
            <Route path={SURGERY_BOARD_PATH} element={<SurgeryBoardRouteElement />} />
            <Route path={BLOCK_SCHEDULE_PATH} element={<BlockScheduleRouteElement />} />
            </Route>
        </Routes>
    );
}

export default App;
